import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PublicNav from "../components/PublicNav";
import {
  Users,
  Calendar,
  BarChart3,
  Zap,
  Shield,
  Globe,
  Clock,
  Bot,
  ArrowRight,
  Sparkles,
  Mail,
} from "lucide-react";

export default function FeaturesPage() {
  const mainFeatures = [
    {
      icon: Bot,
      title: "AI-Powered Conversations",
      description: "Our intelligent agents understand context, remember conversation history, and provide natural, human-like responses to your customers.",
      color: "#6366f1"
    },
    {
      icon: Users,
      title: "Automatic Lead Capture",
      description: "Never miss a potential customer. The AI automatically collects contact information and qualifies leads during conversations.",
      color: "#8b5cf6"
    },
    {
      icon: Calendar,
      title: "Smart Appointment Booking",
      description: "Let customers book appointments directly through WhatsApp. Integrates with your calendar to show real-time availability.",
      color: "#a855f7"
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Track conversations, response rates, lead quality, and customer satisfaction with detailed analytics and insights.",
      color: "#6366f1"
    },
    {
      icon: Mail,
      title: "Automated Email Outreach",
      description: "Your AI agent can automatically send personalized emails to customers - follow-ups, confirmations, promotions, and more.",
      color: "#8b5cf6"
    }
  ];

  const additionalFeatures = [
    { icon: Clock, text: "24/7 Availability", description: "Your AI agent never sleeps" },
    { icon: Globe, text: "Multi-language Support", description: "English, Swahili, and more" },
    { icon: Shield, text: "Secure & Private", description: "Enterprise-grade security" },
    { icon: Zap, text: "10-Minute Setup", description: "No coding required" },
  ];

  return (
    <>
      <SEO
        title="Features"
        description="Discover Botibonga's powerful AI chatbot features: 24/7 customer support, automatic lead capture, smart appointment booking, and detailed analytics."
        path="/features"
      />

      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)' }}>
        <PublicNav />

        {/* Hero */}
        <section className="px-6 py-16 lg:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6"
                 style={{
                   background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                   border: '1px solid rgba(99, 102, 241, 0.3)'
                 }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: '#6366f1' }} />
              <span style={{ color: '#6366f1' }}>Powerful Features</span>
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold mb-6" style={{ color: '#1e293b' }}>
              Everything you need to{" "}
              <span style={{
                background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>automate customer support</span>
            </h1>

            <p className="text-lg mb-8" style={{ color: '#64748b' }}>
              Build intelligent WhatsApp chatbots that handle inquiries, capture leads,
              and book appointments while you focus on growing your business.
            </p>
          </div>
        </section>

        {/* Main Features */}
        <section className="px-6 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {mainFeatures.map((feature, index) => (
                <div key={index} className="p-6 rounded-xl transition-all duration-300 hover:-translate-y-1"
                     style={{
                       background: '#ffffff',
                       border: '1px solid #e2e8f0',
                       boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                     }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                       style={{ background: `${feature.color}15` }}>
                    <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2" style={{ color: '#1e293b' }}>{feature.title}</h3>
                  <p style={{ color: '#64748b' }}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Features */}
        <section className="px-6 py-16" style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold text-center mb-12" style={{ color: '#1e293b' }}>
              And much more...
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {additionalFeatures.map((feature, index) => (
                <div key={index} className="p-5 rounded-xl text-center"
                     style={{
                       background: '#ffffff',
                       border: '1px solid #e2e8f0'
                     }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3"
                       style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)' }}>
                    <feature.icon className="w-5 h-5" style={{ color: '#6366f1' }} />
                  </div>
                  <h4 className="font-semibold mb-1" style={{ color: '#1e293b' }}>{feature.text}</h4>
                  <p className="text-sm" style={{ color: '#64748b' }}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4" style={{ color: '#1e293b' }}>
              Ready to get started?
            </h2>
            <p className="mb-8" style={{ color: '#64748b' }}>
              Create your first AI agent in minutes. No credit card required.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium transition-all hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                  }}>
              Start Building Free
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-8" style={{ borderTop: '1px solid #e2e8f0' }}>
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm" style={{ color: '#94a3b8' }}>
              © {new Date().getFullYear()} Botibonga. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-sm transition-colors" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                    onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
                Terms
              </Link>
              <Link to="/privacy" className="text-sm transition-colors" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                    onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
                Privacy
              </Link>
              <Link to="/contact" className="text-sm transition-colors" style={{ color: '#94a3b8' }}
                    onMouseEnter={(e) => e.target.style.color = '#6366f1'}
                    onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
                Contact
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
